// The iOS app's parts of the site: starting its native side, the one-time
// "turn on notifications" bar, and the notifications card in settings.
// None of it shows on the web.
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Bell, BellOff } from 'lucide-react'
import { Button } from '../ui/Button.jsx'
import { Card, Container, Eyebrow } from '../ui/primitives.jsx'
import { useToast } from '../ui/Toast.jsx'
import { disablePush, enablePush, isNativeApp, markPushAsked, pushAsked, pushPermission, pushToken, startNativeApp } from '../../lib/native.js'
import { useSession } from '../../lib/session.jsx'

// Listens for the phone's push token and for tapped notifications.
export function NativeAppBridge() {
  const navigate = useNavigate()
  const { user } = useSession()
  const signedIn = Boolean(user)
  useEffect(() => {
    if (!isNativeApp()) return undefined
    let stop = () => {}
    let cancelled = false
    const start = async () => {
      const cleanup = await startNativeApp({ navigate, signedIn })
      if (cancelled) cleanup()
      else stop = cleanup
    }
    start()
    return () => {
      cancelled = true
      stop()
    }
  }, [navigate, signedIn])
  return null
}

function usePushPermission(enabled) {
  const [permission, setPermission] = useState(null)
  useEffect(() => {
    if (!enabled) return undefined
    let active = true
    pushPermission().then((p) => active && setPermission(p))
    return () => {
      active = false
    }
  }, [enabled])
  return [permission, setPermission]
}

// Asked once, after signing in, in words rather than straight with iOS's prompt.
export function PushPrompt() {
  const { user, meta } = useSession()
  const toast = useToast()
  const show = isNativeApp() && Boolean(user) && Boolean(meta?.push) && !pushAsked()
  const [permission] = usePushPermission(show)
  const [hidden, setHidden] = useState(false)
  if (!show || hidden || permission !== 'prompt') return null
  async function turnOn() {
    setHidden(true)
    if (await enablePush()) toast.success('Notifications are on.')
  }
  return (
    <div className="border-b border-primary-500/25 bg-primary-500/10">
      <Container className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 text-sm">
        <Bell aria-hidden="true" className="size-4 text-primary-300" />
        <span className="min-w-0 flex-1">Get a notification when check-in opens, your match is ready or someone invites you.</span>
        <span className="flex gap-2">
          <Button size="sm" onClick={turnOn}>
            Turn on
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              markPushAsked()
              setHidden(true)
            }}
          >
            Not now
          </Button>
        </span>
      </Container>
    </div>
  )
}

// Settings: this phone's notifications.
export function PushSettingsCard() {
  const { meta } = useSession()
  const toast = useToast()
  const shown = isNativeApp() && Boolean(meta?.push)
  const [permission, setPermission] = usePushPermission(shown)
  const [on, setOn] = useState(() => Boolean(pushToken()))
  const [busy, setBusy] = useState(false)
  if (!shown || permission === null) return null

  async function turnOn() {
    setBusy(true)
    try {
      const granted = await enablePush()
      setPermission(granted ? 'granted' : 'denied')
      setOn(granted)
      if (granted) toast.success('Notifications are on.')
    } finally {
      setBusy(false)
    }
  }
  async function turnOff() {
    setBusy(true)
    try {
      await disablePush()
      setOn(false)
      toast.success('Notifications are off on this iPhone.')
    } finally {
      setBusy(false)
    }
  }

  const active = permission === 'granted' && on
  return (
    <Card as="section" className="p-5 sm:p-6" aria-labelledby="push-title">
      <Eyebrow as="h2" id="push-title">
        Notifications on this iPhone
      </Eyebrow>
      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          {active ? <Bell aria-hidden="true" className="mt-0.5 size-5 text-emerald-300" /> : <BellOff aria-hidden="true" className="mt-0.5 size-5 text-ink-muted" />}
          <div>
            <p className="font-medium">{active ? 'On' : 'Off'}</p>
            <p className="mt-1 max-w-xl text-sm text-ink-muted">
              {permission === 'denied'
                ? 'iOS has notifications switched off for Pred Hub. Turn them on in your iPhone’s Settings, under Pred Hub, then come back.'
                : 'Everything in your notifications here also comes to this iPhone: invites, check-in opening, matches ready, results and more.'}
            </p>
          </div>
        </div>
        {permission !== 'denied' &&
          (active ? (
            <Button variant="secondary" disabled={busy} onClick={turnOff}>
              Turn off
            </Button>
          ) : (
            <Button disabled={busy} onClick={turnOn}>
              Turn on
            </Button>
          ))}
      </div>
    </Card>
  )
}
